import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@core/environments/environment';
import { firstValueFrom } from 'rxjs';
import { initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  deleteToken,
  isSupported,
  Messaging,
  MessagePayload,
} from 'firebase/messaging';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private BASE_URL = environment.ADMIN_BASE_URL + '/v1/notifications';

  private messaging: Messaging | null = null;

  async requestPermissionAndRegister(): Promise<void> {
    if (!(await this.canUseMessaging())) return;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
    await this.registerToken();
  }

  // Silent refresh on authenticated load — only if permission is already granted.
  async refreshTokenIfGranted(): Promise<void> {
    if (!(await this.canUseMessaging())) return;
    if (Notification.permission !== 'granted') return;
    await this.registerToken();
  }

  // Subscribe to messages received while the app is in the foreground.
  onForegroundMessage(cb: (payload: MessagePayload) => void): void {
    const messaging = this.getMessagingInstance();
    if (!messaging) return;
    onMessage(messaging, cb);
  }

  async unregisterLocally(): Promise<void> {
    try {
      const messaging = this.getMessagingInstance();
      if (messaging) await deleteToken(messaging);
    } catch {
   
    }
  }

  // ─────────────────────────────────────────────────────────────────────────

  private async registerToken(): Promise<void> {
    const messaging = this.getMessagingInstance();
    if (!messaging) {
      console.warn('[push] messaging unavailable — skipping token registration');
      return;
    }

    // Stage 1: obtain the FCM token (service worker + VAPID).
    let token: string | null = null;
    try {
      const swUrl = `/firebase-messaging-sw.js?firebaseConfig=${encodeURIComponent(
        JSON.stringify(environment.firebase)
      )}`;
      const serviceWorkerRegistration =
        await navigator.serviceWorker.register(swUrl);

      token = await getToken(messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration,
      });
    } catch (err) {
      console.error('[push] failed to obtain FCM token', err);
      return;
    }

    if (!token) {
      console.warn('[push] getToken returned empty — permission/VAPID issue');
      return;
    }

    // Stage 2: register the token with the backend.
    try {
      await firstValueFrom(
        this.http.post(`${this.BASE_URL}/create-fmc-token`, {
          FMC_token: token,
          platform: 'WEB',
        })
      );
    } catch (err) {
      console.error('[push] create-fmc-token request failed', err);
    }
  }

  private getMessagingInstance(): Messaging | null {
    if (this.messaging) return this.messaging;
    try {
      const app = initializeApp(environment.firebase);
      this.messaging = getMessaging(app);
      return this.messaging;
    } catch {
      return null;
    }
  }

  private async canUseMessaging(): Promise<boolean> {
    if (!this.isAdminAuthenticated()) return false;
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
      return false;
    }
    try {
      return await isSupported();
    } catch {
      return false;
    }
  }

  private isAdminAuthenticated(): boolean {
    return !!this.authService.getToken() && !this.authService.tokenIsExpired();
  }
}
